import React, { PropsWithChildren, createContext, useRef } from 'react';
import { StoreApi, UseBoundStore } from 'zustand';
import {
  createAppStore,
  CreateAppStoreType,
  StoreContextPropType,
} from './createStore';

export const StoreContext = createContext<
  UseBoundStore<StoreApi<any>> & StoreContextPropType
>(null);

export interface StoreProviderProps<T> extends CreateAppStoreType<T> {
  /**
   * Listener for subscribing to store state changes.
   * @param state - Current state
   * @param prevState - Prev state
   * @returns {void}
   */
  subscribe?: (state: T, prevState: T) => void;
}

export const ApiContext = createContext(null);

/**
 * @description StoroProvider component. This component is used to provide the store to the application.
 * @prop {React.ReactNode} children - The children of the component
 * @prop {any} initialState - Initial state of the store.
 * @prop {Function} createSlice - Function to create the state and actions of the store.
 * @prop {any} persistConfig - Persist configuration of the store.
 * @prop {GeneratorFunction[]} sagas - Sagas of the store
 * @category Component
 */
export function StoreProvider<T extends object>({
  children,
  initialState,
  createSlice,
  api,
  sagas,
  middleware,
  sagasContext = {},
  context = {},
  subscribe,
}: PropsWithChildren<StoreProviderProps<T>>) {
  const storeRef = useRef<UseBoundStore<StoreApi<T>> & StoreContextPropType>(
    null
  );
  if (!storeRef.current) {
    const store = createAppStore<T>({
      initialState,
      createSlice,
      sagas,
      api,
      middleware,
      sagasContext,
      context,
    });
    if (subscribe) store.subscribe(subscribe);
    storeRef.current = Object.assign(store, context || {}, sagasContext || {});
  }
  return (
    <StoreContext.Provider value={storeRef.current}>
      <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
    </StoreContext.Provider>
  );
}
